import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  VStack,
  Text,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Card,
  CardBody,
  Badge,
  Divider,
  Alert,
  AlertIcon,
  useToast,
  Spinner,
  Center,
  IconButton,
  Flex
} from '@chakra-ui/react';
import { ArrowBackIcon } from '@chakra-ui/icons';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { doc, getDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { format } from 'date-fns';
import { NOTIFICATION_CONSTANTS } from '../../constants/notifications';
import { formatCurrency } from '../../utils/currencyUtils';

interface LineItem {
  name: string;
  amount: number;
}

interface SalesOrder {
  id: string;
  salesOrderNumber: string;
  companyName: string;
  created_at: any; // Firestore timestamp
  contractPrice: number;
  wayBillNumber: string;
  gpr: number;
  lineItems: LineItem[];
  totalCost: number;
  isApproved?: boolean;
  isAutoApproved?: boolean;
  isRejected?: boolean;
  approved_at?: any;
  approvedBy?: string;
  rejected_at?: any;
  rejectedBy?: string;
}

const SalesOrder: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const [salesOrder, setSalesOrder] = useState<SalesOrder | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<'approve' | 'reject' | null>(null);
  
  // Check if user came from notifications page
  const fromNotifications = location.state?.fromNotifications === true;

  console.log('📄 SalesOrder component mounted - URL params:', { id });
  console.log('📄 SalesOrder component - current location:', window.location.pathname);

  useEffect(() => {
    const fetchSalesOrder = async () => {
      console.log('🔍 SalesOrder component - ID from params:', id);
      if (!id) {
        console.error('❌ No sales order ID provided');
        toast({
          title: 'Error',
          description: 'No sales order ID provided',
          status: 'error',
          duration: NOTIFICATION_CONSTANTS.TOAST_DURATION,
          isClosable: true,
        });
        navigate('/dashboard');
        return;
      }

      try {
        const salesOrderDoc = await getDoc(doc(db, 'salesOrders', id));
        
        if (!salesOrderDoc.exists()) {
          toast({
            title: 'Error',
            description: 'Sales order not found',
            status: 'error',
            duration: NOTIFICATION_CONSTANTS.TOAST_DURATION,
            isClosable: true,
          });
          navigate('/dashboard');
          return;
        }

        const data = salesOrderDoc.data() as Omit<SalesOrder, 'id'>;
        setSalesOrder({ id: salesOrderDoc.id, ...data });
      } catch (error) {
        console.error('Error fetching sales order:', error);
        toast({
          title: 'Error',
          description: 'Failed to load sales order',
          status: 'error',
          duration: NOTIFICATION_CONSTANTS.TOAST_DURATION,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchSalesOrder();
  }, [id, navigate, toast]);

  const handleApprove = async () => {
    if (!salesOrder) return;

    setActionLoading('approve');
    try {
      await updateDoc(doc(db, 'salesOrders', salesOrder.id), {
        isApproved: true,
        approved_at: new Date(),
        // TODO: Add current user ID when auth context is available
        // approvedBy: currentUser.uid
      });

      // Update related notifications to reflect approval status
      try {
        const notificationsQuery = query(
          collection(db, 'notifications'),
          where('data.salesOrderId', '==', salesOrder.id),
          where('type', '==', 'sales_order_approver')
        );
        const notificationsSnapshot = await getDocs(notificationsQuery);
        
        const updatePromises = notificationsSnapshot.docs.map(notifDoc => 
          updateDoc(notifDoc.ref, {
            title: 'Approved',
            'data.approvalStatus': 'approved'
          })
        );
        
        await Promise.all(updatePromises);
        console.log(`Updated ${notificationsSnapshot.size} notification(s) to Approved status`);
      } catch (notifError) {
        console.error('Error updating notifications:', notifError);
        // Don't fail the approval if notification update fails
      }

      toast({
        title: 'Success',
        description: 'Sales order approved successfully',
        status: 'success',
        duration: NOTIFICATION_CONSTANTS.TOAST_DURATION,
        isClosable: true,
      });

      // Update local state
      setSalesOrder(prev => prev ? {
        ...prev,
        isApproved: true,
        approved_at: new Date()
      } : null);

    } catch (error) {
      console.error('Error approving sales order:', error);
      toast({
        title: 'Error',
        description: 'Failed to approve sales order',
        status: 'error',
        duration: NOTIFICATION_CONSTANTS.TOAST_DURATION,
        isClosable: true,
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async () => {
    if (!salesOrder) return;

    setActionLoading('reject');
    try {
      await updateDoc(doc(db, 'salesOrders', salesOrder.id), {
        isApproved: false,
        isRejected: true,
        rejected_at: new Date(),
        approved_at: null,
        // TODO: Add current user ID when auth context is available
        // rejectedBy: currentUser.uid
      });

      toast({
        title: 'Success',
        description: 'Sales order rejected',
        status: 'success',
        duration: NOTIFICATION_CONSTANTS.TOAST_DURATION,
        isClosable: true,
      });

      // Update local state
      setSalesOrder(prev => prev ? {
        ...prev,
        isApproved: false,
        isRejected: true,
        rejected_at: new Date()
      } : null);

    } catch (error) {
      console.error('Error rejecting sales order:', error);
      toast({
        title: 'Error',
        description: 'Failed to reject sales order',
        status: 'error',
        duration: NOTIFICATION_CONSTANTS.TOAST_DURATION,
        isClosable: true,
      });
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (date: any) => {
    if (!date) return 'N/A';
    if (date.toDate) {
      return format(date.toDate(), 'MMM dd, yyyy HH:mm');
    }
    return format(new Date(date), 'MMM dd, yyyy HH:mm');
  };

  if (loading) {
    return (
      <Box minH="100vh" bgGradient="linear(135deg, blue.50, purple.50, pink.50)">
        <Container maxW={{ base: "100%", md: "4xl" }} py={{ base: 4, md: 8 }} px={{ base: 4, md: 8 }}>
          <Center>
            <VStack spacing={4}>
              <Spinner size="xl" color="blue.500" thickness="4px" />
              <Text fontSize={{ base: "md", md: "lg" }}>Loading sales order...</Text>
            </VStack>
          </Center>
        </Container>
      </Box>
    );
  }

  if (!salesOrder) {
    return (
      <Box minH="100vh" bgGradient="linear(135deg, blue.50, purple.50, pink.50)">
        <Container maxW={{ base: "100%", md: "4xl" }} py={{ base: 4, md: 8 }} px={{ base: 4, md: 8 }}>
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            <Text fontSize={{ base: "sm", md: "md" }}>Sales order not found</Text>
          </Alert>
        </Container>
      </Box>
    );
  }

  const isAlreadyProcessed = salesOrder.isApproved === true || salesOrder.isRejected === true;

  return (
    <Box minH="100vh" bgGradient="linear(135deg, blue.50, purple.50, pink.50)">
      <Container maxW={{ base: "100%", md: "4xl" }} py={{ base: 4, md: 8 }} px={{ base: 4, md: 8 }}>
        <VStack spacing={{ base: 4, md: 6 }} align="stretch">
          {/* Header */}
          <Flex align="center" gap={{ base: 2, md: 4 }}>
            <IconButton
              aria-label="Go back"
              icon={<ArrowBackIcon />}
              onClick={() => navigate(fromNotifications ? '/notifications' : '/dashboard')}
              variant="ghost"
              size={{ base: "md", md: "lg" }}
            />
            <VStack align="start" spacing={1} flex={1}>
              <Text fontSize={{ base: "xl", md: "2xl" }} fontWeight="bold" color="gray.800">
                Sales Order Details
              </Text>
              <VStack align="start" spacing={2} w="full">
                <Text fontSize={{ base: "md", md: "lg" }} color="gray.600">
                  {salesOrder.salesOrderNumber}
                </Text>
                {salesOrder.isApproved === true && (
                  <Badge colorScheme="green" size={{ base: "sm", md: "md" }}>{salesOrder.isAutoApproved ? 'Auto-Approved' : 'Approved'}</Badge>
                )}
                {salesOrder.isRejected === true && (
                  <Badge colorScheme="red" size={{ base: "sm", md: "md" }}>Rejected</Badge>
                )}
              </VStack>
            </VStack>
          </Flex>

          {/* Sales Order Information */}
          <Card>
            <CardBody p={{ base: 4, md: 6 }}>
              <VStack spacing={4} align="stretch">
                <Text fontSize={{ base: "md", md: "lg" }} fontWeight="semibold" color="gray.700">
                  Order Information
                </Text>
                
                <VStack spacing={3} align="stretch">
                  <VStack align="stretch" spacing={1}>
                    <Text fontWeight="medium" color="gray.600" fontSize={{ base: "sm", md: "md" }}>Company:</Text>
                    <Text fontWeight="bold" fontSize={{ base: "md", md: "lg" }}>{salesOrder.companyName}</Text>
                  </VStack>
                  
                  <VStack align="stretch" spacing={1}>
                    <Text fontWeight="medium" color="gray.600" fontSize={{ base: "sm", md: "md" }}>Created:</Text>
                    <Text fontSize={{ base: "sm", md: "md" }}>{formatDate(salesOrder.created_at)}</Text>
                  </VStack>
                  
                  <VStack align="stretch" spacing={1}>
                    <Text fontWeight="medium" color="gray.600" fontSize={{ base: "sm", md: "md" }}>Contract Price:</Text>
                    <Text fontWeight="bold" color="green.600" fontSize={{ base: "lg", md: "xl" }}>
                      {formatCurrency(salesOrder.contractPrice)}
                    </Text>
                  </VStack>
                  
                  <VStack align="stretch" spacing={1}>
                    <Text fontWeight="medium" color="gray.600" fontSize={{ base: "sm", md: "md" }}>Waybill Number:</Text>
                    <Text fontFamily="mono" fontSize={{ base: "sm", md: "md" }}>{salesOrder.wayBillNumber}</Text>
                  </VStack>
                  
                  <VStack align="stretch" spacing={1}>
                    <Text fontWeight="medium" color="gray.600" fontSize={{ base: "sm", md: "md" }}>GPR:</Text>
                    <Text fontSize={{ base: "sm", md: "md" }}>
                      {typeof salesOrder.gpr === 'number'
                        ? salesOrder.gpr.toFixed(2)
                        : 'N/A'
                      }%
                    </Text>
                  </VStack>
                </VStack>
              </VStack>
            </CardBody>
          </Card>

            {/* Line Items */}
            <Card>
              <CardBody p={{ base: 4, md: 6 }}>
                <VStack spacing={4} align="stretch">
                  <Text fontSize={{ base: "md", md: "lg" }} fontWeight="semibold" color="gray.700">
                    Line Items
                  </Text>
                  
                  {/* Mobile-friendly line items */}
                  <Box display={{ base: "block", md: "none" }}>
                    <VStack spacing={3}>
                      {salesOrder.lineItems?.map((item, index) => (
                        <Box key={index} w="full" p={3} bg="gray.50" borderRadius="md">
                          <VStack align="stretch" spacing={1}>
                            <Text fontWeight="medium" fontSize="sm">{item.name}</Text>
                            <Text fontWeight="bold" color="green.600" fontSize="lg" textAlign="right">
                              {formatCurrency(item.amount)}
                            </Text>
                          </VStack>
                        </Box>
                      ))}
                    </VStack>
                  </Box>

                  {/* Desktop table */}
                  <Box display={{ base: "none", md: "block" }}>
                    <TableContainer>
                      <Table variant="simple" size="sm">
                        <Thead>
                          <Tr>
                            <Th>Item Name</Th>
                            <Th isNumeric>Amount</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {salesOrder.lineItems?.map((item, index) => (
                            <Tr key={index}>
                              <Td>{item.name}</Td>
                              <Td isNumeric fontWeight="medium">
                                {formatCurrency(item.amount)}
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </TableContainer>
                  </Box>
                  
                  <Divider />
                  
                  <Flex justify="space-between" align="center" pt={2} direction={{ base: "column", md: "row" }} gap={{ base: 2, md: 0 }}>
                    <Text fontSize={{ base: "md", md: "lg" }} fontWeight="bold" color="gray.700">
                      Total Cost:
                    </Text>
                    <Text fontSize={{ base: "xl", md: "xl" }} fontWeight="bold" color="green.600">
                      {formatCurrency(salesOrder.totalCost)}
                    </Text>
                  </Flex>
                </VStack>
              </CardBody>
            </Card>

            {/* Action Buttons */}
            {!isAlreadyProcessed && (
              <Card>
                <CardBody p={{ base: 4, md: 6 }}>
                  <VStack spacing={4}>
                    <Text fontSize={{ base: "md", md: "lg" }} fontWeight="semibold" color="gray.700">
                      Approval Actions
                    </Text>
                    
                    <VStack spacing={{ base: 3, md: 4 }} w="full">
                      <Button
                        colorScheme="green"
                        size={{ base: "lg", md: "lg" }}
                        w="full"
                        onClick={handleApprove}
                        isLoading={actionLoading === 'approve'}
                        loadingText="Approving..."
                        fontSize={{ base: "md", md: "lg" }}
                        py={{ base: 6, md: 8 }}
                      >
                        Approve
                      </Button>
                      
                      <Button
                        colorScheme="red"
                        variant="outline"
                        size={{ base: "lg", md: "lg" }}
                        w="full"
                        onClick={handleReject}
                        isLoading={actionLoading === 'reject'}
                        loadingText="Rejecting..."
                        fontSize={{ base: "md", md: "lg" }}
                        py={{ base: 6, md: 8 }}
                      >
                        Reject
                      </Button>
                    </VStack>
                  </VStack>
                </CardBody>
              </Card>
            )}

            {/* Already Processed Message */}
            {salesOrder.isApproved === true && (
              <Alert status="success" borderRadius="md" p={{ base: 4, md: 6 }}>
                <AlertIcon />
                <Box>
                  <Text fontWeight="bold" fontSize={{ base: "sm", md: "md" }}>
                    This sales order has already been {salesOrder.isAutoApproved ? 'auto-approved' : 'approved'}.
                  </Text>
                  {salesOrder.approved_at && (
                    <Text fontSize={{ base: "xs", md: "sm" }} mt={1}>
                      Approved on: {formatDate(salesOrder.approved_at)}
                    </Text>
                  )}
                </Box>
              </Alert>
            )}
            {salesOrder.isRejected === true && (
              <Alert status="warning" borderRadius="md" p={{ base: 4, md: 6 }}>
                <AlertIcon />
                <Box>
                  <Text fontWeight="bold" fontSize={{ base: "sm", md: "md" }}>
                    This sales order has already been rejected.
                  </Text>
                  {salesOrder.rejected_at && (
                    <Text fontSize={{ base: "xs", md: "sm" }} mt={1}>
                      Rejected on: {formatDate(salesOrder.rejected_at)}
                    </Text>
                  )}
                </Box>
              </Alert>
            )}
          </VStack>
        </Container>
      </Box>
    );
};

export default SalesOrder;
